<template>
  <Modal class="max-w-[400px]!">
    <Modal.Title>Confirm your payment</Modal.Title>
    <Modal.Body>
      <PaymentSummary
        :lines="lines"
        :total="amount"
        :payment-method="billing?.paymentMethod"
        :error="errorMessage"
        @change-payment="handleChangeClick"
      />
    </Modal.Body>
    <Modal.Footer layout="vertical">
      <Button
        variant="primary"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="!billing?.paymentMethod"
        :loading="loading"
        @click="handleClick"
      >
        Agree and pay
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Modal, Button, useModal } from '@dacr/library'

import { useDevice } from '@/shared/composables/useDevice'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useBillingStore } from '@/features/billing/store'
import PaymentSummary, { type PaymentSummaryLine } from './PaymentSummary.vue'
import {
  BillingQueryDocument,
  InvoiceStatus,
  PlanStatus,
  useInvoicePdfQuery,
  usePayMutation,
  UserPermission,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { formatPrice } from '@/shared/utils/money'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useWebviewStore } from '@/shared/stores/webview'
import { getStripe } from '@/shared/services/stripe'
import { apolloClient } from '@/shared/graphql/client'
import { useUserStore } from '@/shared/stores/user'
import gql from 'graphql-tag'

const { triggerEvent } = useModal()
const { isMobile } = useDevice()

const { mutate: pay } = usePayMutation()

const billingStore = useBillingStore()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()
const userStore = useUserStore()

const loading = ref(false)
const error = ref('')

const billing = computed(() => billingStore.billing)
const invoice = computed(() =>
  billing.value?.invoices.invoices.find(
    (invoice) => invoice.status === InvoiceStatus.Overdue
  )
)
const amount = computed(() => {
  return formatPrice({
    amount: (invoice.value?.total || 0) / 100,
    currency: invoice.value?.currency,
  })
})

const enabled = computed(() => !!invoice.value?.id)

const { result } = useInvoicePdfQuery(
  () => ({
    invoiceId: invoice.value?.id || '',
  }),
  { enabled }
)

const openInvoice = () => {
  if (!result.value?.invoicePdf.url) return
  window.open(result.value?.invoicePdf.url, '_blank')
}

const lines = computed<PaymentSummaryLine[]>(() => [
  {
    label: `Invoice #${invoice.value?.id ?? ''}`,
    actionLabel: 'View',
    onAction: openInvoice,
  },
])

const errorMessage = computed(() =>
  error.value ? `Could not pay balance: ${error.value}` : ''
)

const handleChangeClick = () => {
  openDialog(Dialog.AddPaymentMethod, { stack: true })
  error.value = ''
}

const handleRequiresAction = async (clientSecret: string) => {
  if (webviewStore.isActive) {
    return
  }

  const stripe = await getStripe()
  if (!stripe) return

  const { error: stripeError } = await stripe.confirmCardPayment(clientSecret)

  if (stripeError) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Payment authentication failed',
      error: new Error(stripeError.message),
    })
    return
  }

  if (!userStore.user) return

  apolloClient.cache.updateFragment(
    {
      id: apolloClient.cache.identify(userStore.user),
      fragment: gql`
        fragment UpdateUser on User {
          permissions
        }
      `,
    },
    (response) => {
      if (!response) return
      return {
        permissions: userStore.user?.permissions.concat(UserPermission.Pro),
      }
    }
  )

  apolloClient.cache.updateQuery<BillingQueryQuery>(
    { query: BillingQueryDocument },
    (response) => {
      if (!response) return
      return {
        ...response,
        billing: {
          ...response.billing,
          plan: {
            ...response.billing.plan,
            status: PlanStatus.Active,
            dateInfo: {
              __typename: 'BillingPlanDateInfo',
              date: response.billing.upcomingDetails.upcomingBilling?.date,
              prefix: 'Renews on',
            },
          },
          billingAlert: null,
        },
      }
    }
  )
}

const handleClick = async () => {
  try {
    loading.value = true

    const response = await pay(
      {},
      {
        update(cache) {
          cache.updateQuery<BillingQueryQuery>(
            { query: BillingQueryDocument },
            (response) => {
              if (!response) return
              return {
                ...response,
                billing: {
                  ...response.billing,
                  billingAlert: null,
                },
              }
            }
          )
        },
      }
    )

    if (response?.data?.pay.paymentMethod?.requiresAction) {
      await handleRequiresAction(
        response.data.pay.paymentMethod.requiresAction.clientSecret
      )
    }

    triggerEvent('success')
  } catch (err) {
    loading.value = false
    error.value = (err as Error).message
  }
}
</script>
